import { useState } from "react";
import videojs from "video.js/core.es.js";
import { chainStore } from "@/stores/chain/store";
import type { ChainNavigation } from "@/components/player/useChainNavigation";
import type { PlayerSource } from "@/components/player/usePlayerSource";
import type { VideoFullscreen } from "@/components/player/useVideoFullscreen";
import type { VideoTracking } from "@/components/player/useVideoTracking";
import type {
  VideoJsSelf,
  VideoPlayer,
} from "@/components/player/videoJsTypes";

// Attaches and detaches this mount's listeners and control-bar decorations.
function createPlayerEvents(
  self: VideoJsSelf,
  tracking: VideoTracking,
  fullscreen: VideoFullscreen,
  chain: ChainNavigation,
  source: PlayerSource,
) {
  // Attach this mount's listeners + control-bar decorations to `self.player`.
  // Runs on both the create and the adopt (rotate re-host) paths.
  // `detachFromPlayer` is the exact inverse, run on unmount so the previous
  // mount's handlers never fire on the persistent player.
  function attachToPlayer(player: VideoPlayer, playerIsReady: boolean) {
    attachPlayerListeners(player);
    if (playerIsReady) {
      attachControlBarDecorations();
    } else {
      player.ready(() => {
        if (self.player && !self.player.isDisposed()) {
          attachControlBarDecorations();
        }
      });
    }
  }

  function attachPlayerListeners(player: VideoPlayer) {
    player.on("play", tracking.onPlayerPlay);
    player.on("playing", tracking.onPlayerPlaying);
    player.on("pause", tracking.onPlayerPause);
    player.on("ended", tracking.onPlayerEnded);
    player.on("loadstart", tracking.onPlayerLoadStart);
    player.on("loadeddata", tracking.onPlayerMediaReady);
    player.on("canplay", tracking.onPlayerMediaReady);
    player.on("seeking", tracking.onPlayerSeeking);
    player.on("seeked", tracking.onPlayerSeeked);
    player.on("error", tracking.onPlayerError);
    player.on("stalled", tracking.onPlayerStalled);
    player.on("resolutionchange", tracking.onResolutionChanged);
    player.on("fullscreenchange", fullscreen.onFullscreenChange);
    // Keep the video's rendered content rect (letterbox/pillarbox insets)
    // published as CSS vars so overlays (advertiser logo) stay on the video.
    player.on("loadedmetadata", updateVideoContentRect);
    player.on("loadeddata", updateVideoContentRect);
    player.on("playerresize", updateVideoContentRect);
    // timeupdate is normally (re)bound in onPlayerPlaying, but on an adopt the
    // video is already playing and no fresh 'playing' fires — so bind it here too
    // (off-then-on is dup-safe) or TIME_SYNC would stop after a rotate.
    player.off("timeupdate", tracking.onTimeUpdate);
    player.on("timeupdate", tracking.onTimeUpdate);
    window.addEventListener("resize", updateVideoContentRect);
    window.addEventListener("orientationchange", updateVideoContentRect);
    // Learn the video's aspect from the poster up front so the logo anchors to
    // the video area even before the first frame decodes / on error.
    preloadPosterAspect();
    videojs.on(self.videoElement, "contextmenu", onContextMenu);

    // Chain prev/next arrows in the control bar (between play + volume).
    self.unsubscribeChain = chainStore.subscribe(chain.onChainStoreChange);
  }

  // Overlay the advertiser logo INSIDE the `.video-js` element so it survives
  // every fullscreen path. Desktop fullscreen uses video.js's native fullscreen,
  // which fullscreens only `.video-js` — a logo rendered by React as a sibling
  // is outside that element and vanishes. Injecting it here (a direct child of
  // `player.el()`) keeps it on the video in windowed AND fullscreen, in every
  // landscape layout. Reuses the global `.l-topbar`/`.l-topbar-logo` styles; CSS
  // hides it in portrait (which shows the logo in its own stacked header instead).
  // Idempotent: safe to call on create, adopt, and every update; a chain advance
  // creates a fresh player, so the overlay is re-injected for the new
  // experience's logo.
  function ensureLogoOverlay() {
    if (!self.player || self.player.isDisposed()) {
      return;
    }
    const el = self.player.el();
    if (!el) {
      return;
    }
    const experienceData =
      self.props.experienceState && self.props.experienceState.experienceData;
    const src = experienceData ? experienceData.getLogoImageUrl() : null;
    let bar = el.querySelector<HTMLElement>(".pix-player-logo");
    if (!src) {
      if (bar) {
        bar.style.display = "none";
      }
      return;
    }
    if (!bar) {
      const newBar = document.createElement("div");
      bar = newBar;
      // `l-topbar` for the shared positioning styles; `pix-player-logo` marks it
      // as this injected copy (so we find it again without matching anything else).
      newBar.className = "l-topbar pix-player-logo";
      newBar.setAttribute("aria-hidden", "true");
      const logo = document.createElement("div");
      logo.className = "l-topbar-logo";
      const img = document.createElement("img");
      img.alt = "";
      // Hide on a load error (a replaced logo file can 404) rather than showing a
      // broken-image icon. Record WHICH src failed so subsequent re-renders don't
      // re-show it — the errored state must persist until the src actually changes
      // (ensureLogoOverlay runs on every re-render, not just create/adopt).
      img.addEventListener("error", () => {
        newBar.dataset.erroredSrc = img.getAttribute("src") || "";
        newBar.style.display = "none";
      });
      logo.appendChild(img);
      newBar.appendChild(logo);
      el.appendChild(newBar);
    }
    const img = bar.querySelector("img");
    if (img && img.getAttribute("src") !== src) {
      // New/changed logo — attempt a fresh load and forget any prior failure.
      img.setAttribute("src", src);
      delete bar.dataset.erroredSrc;
    }
    // Show unless THIS src is the one that failed to load.
    bar.style.display = bar.dataset.erroredSrc === src ? "none" : "";
  }

  function attachControlBarDecorations() {
    if (!self.player || self.player.isDisposed() || !self.player.controlBar) {
      return;
    }
    chain.setupChainNavButtons();
    fullscreen.setupFullscreen();
    ensureLogoOverlay();
    const progressControl = self.player.controlBar.getChild("progressControl");
    if (progressControl) {
      // Ensure the progress control can receive pointer events, esp. in iframes
      const progressControlEl = progressControl.el() as HTMLElement;
      if (progressControlEl) {
        progressControlEl.style.pointerEvents = "auto";
        // Ensure touch events work on mobile
        progressControlEl.style.touchAction = "manipulation";
      }
    }
  }

  // Inverse of attachToPlayer: detach every per-mount listener + decoration
  // from the persistent player WITHOUT disposing it, so a rotate remount can
  // re-adopt it. The chain-nav buttons, nav-preview cards and fullscreen-toggle
  // wrapper all close over THIS mount, so they are torn down here and rebuilt
  // fresh (bound to the next mount) on re-host — no duplicates, no stale refs.
  function detachFromPlayer() {
    source.cancelAutoplayFallback();
    self.unsubscribeChain?.();
    window.removeEventListener("resize", updateVideoContentRect);
    window.removeEventListener("orientationchange", updateVideoContentRect);
    fullscreen.detachFullscreen();
    chain.detachChainNav();
    // Detach the per-mount player + element listeners.
    if (self.player) {
      const player = self.player;
      player.off("play", tracking.onPlayerPlay);
      player.off("playing", tracking.onPlayerPlaying);
      player.off("pause", tracking.onPlayerPause);
      player.off("ended", tracking.onPlayerEnded);
      player.off("loadstart", tracking.onPlayerLoadStart);
      player.off("loadeddata", tracking.onPlayerMediaReady);
      player.off("canplay", tracking.onPlayerMediaReady);
      player.off("timeupdate", tracking.onTimeUpdate);
      player.off("seeking", tracking.onPlayerSeeking);
      player.off("seeked", tracking.onPlayerSeeked);
      player.off("error", tracking.onPlayerError);
      player.off("stalled", tracking.onPlayerStalled);
      player.off("resolutionchange", tracking.onResolutionChanged);
      player.off("fullscreenchange", fullscreen.onFullscreenChange);
      player.off("loadedmetadata", updateVideoContentRect);
      player.off("loadeddata", updateVideoContentRect);
      player.off("playerresize", updateVideoContentRect);
      videojs.off(self.videoElement, "contextmenu", onContextMenu);
    }
  }

  function onContextMenu(e: Event) {
    e.preventDefault();
  }

  // Publish the video's rendered content rect as CSS custom properties on the
  // experience container. With object-fit: contain the <video> box fills the
  // player but the actual picture is letterboxed/pillarboxed inside it; overlays
  // anchored to the container edge (e.g. the advertiser logo bar) would otherwise
  // sit out on the surrounding black. `--pix-video-inset-x/y` are the half-bar
  // sizes so those overlays can pad in and stay on the video, any viewport.
  // The poster is the video's own frame, so it shares the video's intrinsic
  // aspect. Preload it once to cache that aspect; updateVideoContentRect falls
  // back to it when the <video> hasn't reported its dimensions yet (still
  // loading) or never does (playback error), so the advertiser logo stays
  // anchored to the video content rect rather than drifting out onto the
  // letterbox black.
  function preloadPosterAspect() {
    try {
      const experienceData = self.props.experienceState.experienceData;
      const url = experienceData && experienceData.getPosterImageUrl();
      if (!url) {
        return;
      }
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          self.posterAspectW = img.naturalWidth;
          self.posterAspectH = img.naturalHeight;
          updateVideoContentRect();
        }
      };
      img.src = url;
    } catch {
      /* best-effort overlay positioning */
    }
  }

  function updateVideoContentRect() {
    try {
      if (!self.player || self.player.isDisposed()) {
        return;
      }
      // MCP landscape has no `.experience-container` — publish the same vars on
      // `.mcp-player` so the in-video logo scales to the card's video box
      // instead of the 900px CSS fallback (which made the overlay huge).
      const container =
        self.player.el().closest<HTMLElement>(".experience-container") ||
        self.player.el().closest<HTMLElement>(".mcp-player");
      const videoEl = self.player
        .el()
        .querySelector<HTMLVideoElement>("video.vjs-tech");
      if (!container || !videoEl) {
        return;
      }
      let vw = videoEl.videoWidth;
      let vh = videoEl.videoHeight;
      if (!vw || !vh) {
        // Video dimensions not available — still loading, or the video errored
        // and will never report them. Fall back to the poster's aspect: it's the
        // same frame, so it letterbox/pillarboxes identically, keeping the logo
        // on the video content rect instead of the black.
        vw = self.posterAspectW;
        vh = self.posterAspectH;
      }
      const box = videoEl.getBoundingClientRect();
      if (!vw || !vh || !box.width || !box.height) {
        return; // no aspect known yet — leave insets at their default (0)
      }
      const scale = Math.min(box.width / vw, box.height / vh);
      const contentW = vw * scale;
      const contentH = vh * scale;
      const insetX = Math.max(0, (box.width - contentW) / 2);
      const insetY = Math.max(0, (box.height - contentH) / 2);
      container.style.setProperty(
        "--pix-video-inset-x",
        `${Math.round(insetX)}px`,
      );
      container.style.setProperty(
        "--pix-video-inset-y",
        `${Math.round(insetY)}px`,
      );
      // The rendered video box (picture area, excluding letterbox bars). The
      // landscape brand logo is sized relative to this box (both dimensions
      // constrained) instead of fixed px, so it scales with the picture.
      container.style.setProperty(
        "--pix-video-content-w",
        `${Math.round(contentW)}px`,
      );
      container.style.setProperty(
        "--pix-video-content-h",
        `${Math.round(contentH)}px`,
      );
    } catch {
      /* best-effort overlay positioning */
    }
  }

  return {
    attachToPlayer,
    detachFromPlayer,
    ensureLogoOverlay,
    updateVideoContentRect,
  };
}

export type PlayerEvents = ReturnType<typeof createPlayerEvents>;

/** The listener attach/detach of one VideoJs mount; stable for the mount. */
export function usePlayerEvents(
  self: VideoJsSelf,
  tracking: VideoTracking,
  fullscreen: VideoFullscreen,
  chain: ChainNavigation,
  source: PlayerSource,
): PlayerEvents {
  const [events] = useState(() =>
    createPlayerEvents(self, tracking, fullscreen, chain, source),
  );
  return events;
}
